// @ts-check
// Barra de abas do painel (doc 16, seção 8.4; contrato 07, seção 8.1). Fora da edição só aparece com mais de uma aba.
// Em edição: "+" cria a aba, o menu da aba aberta renomeia, move para os lados, duplica e exclui, e cada aba aceita
// soltar um widget arrastado pelo grip (move para ela).
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { MAXIMUM_DASHBOARD_TABS } from '../../lib/dashboard-tabs.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/dashboard-tabs.js').DraftTab} DraftTab
 * @typedef {{ onAdd: () => void, onRename: (key: string, title: string) => void, onMove: (key: string, direction: -1 | 1) => void,
 *   onDuplicate: (key: string) => void, onRemove: (key: string) => void }} TabEditActions
 */

/**
 * Título editável no lugar: Enter ou sair do campo grava, Esc desiste.
 * @param {{ value: string, onCommit: (value: string) => void, onCancel: () => void }} props
 */
function TabTitleInput({ value, onCommit, onCancel }) {
  const [draft, setDraft] = useState(value);
  const doneRef = useRef(false);
  const finish = (/** @type {boolean} */ shouldCommit) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (shouldCommit && draft.trim()) onCommit(draft.trim().slice(0, 100));
    else onCancel();
  };
  return html`<input
    class="dashboard-tab-title-input"
    value=${draft}
    maxLength=${100}
    aria-label=${t('dashboardParity.tabs.titleLabel')}
    ref=${(element) => {
      if (element && !element.dataset.focused) {
        element.dataset.focused = 'true';
        requestAnimationFrame(() => element.select());
      }
    }}
    onInput=${(event) => setDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
    onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
      if (event.key === 'Enter') finish(true);
      else if (event.key === 'Escape') finish(false);
      event.stopPropagation();
    }}
    onBlur=${() => finish(true)}
  />`;
}

/**
 * @param {{ tabs: DraftTab[], activeKey: string | null, isEditing: boolean, onSelect: (key: string) => void, actions?: TabEditActions }} props
 */
export function DashboardTabBar({ tabs, activeKey, isEditing, onSelect, actions }) {
  const [renamingKey, setRenamingKey] = useState(/** @type {string | null} */ (null));
  const [menuKey, setMenuKey] = useState(/** @type {string | null} */ (null));
  const menuAnchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  if (tabs.length <= 1 && !isEditing) return null;
  const run = (/** @type {() => void} */ action) => {
    setMenuKey(null);
    action();
  };
  return html`<div class="tab-list dashboard-tab-bar" role="tablist" aria-label=${t('dashboardParity.tabs.label')} data-editing=${isEditing ? 'true' : undefined}>
    ${tabs.map((tab, index) => {
      const isActive = tab.key === activeKey;
      if (isEditing && renamingKey === tab.key && actions) {
        return html`<div key=${tab.key} class="tab-button dashboard-tab" data-active="true">
          <${TabTitleInput} value=${tab.title} onCommit=${(title) => { actions.onRename(tab.key, title); setRenamingKey(null); }} onCancel=${() => setRenamingKey(null)} />
        </div>`;
      }
      return html`<div key=${tab.key} class="dashboard-tab-wrapper" data-dashboard-tab-drop=${isEditing ? tab.key : undefined} data-active=${isActive ? 'true' : undefined}>
        <button
          type="button"
          role="tab"
          class="tab-button dashboard-tab"
          aria-selected=${isActive ? 'true' : 'false'}
          data-active=${isActive ? 'true' : undefined}
          onClick=${() => onSelect(tab.key)}
          onDblClick=${isEditing && actions ? () => setRenamingKey(tab.key) : undefined}
          title=${isEditing ? t('dashboardParity.tabs.renameHint') : undefined}
        >
          <span class="tab-button-content">
            ${tab.icon ? html`<${Icon} name=${tab.icon} size=${16} />` : null}
            <span>${tab.title}</span>
          </span>
        </button>
        ${isEditing && isActive && actions
          ? html`<span class="dashboard-tab-menu" ref=${menuAnchorRef}>
              <${LightIconButton} icon="IconDotsVertical" accent="tertiary" size="small" ariaLabel=${t('dashboardParity.tabs.menu', { title: tab.title })}
                active=${menuKey === tab.key} onClick=${() => setMenuKey(menuKey === tab.key ? null : tab.key)} />
            </span>
            <${Dropdown} anchorRef=${menuAnchorRef} isOpen=${menuKey === tab.key} onClose=${() => setMenuKey(null)} placement="bottom-start" width=${200}>
              <${DropdownMenuItems}>
                <${MenuItem} icon="IconPencil" text=${t('dashboardParity.tabs.rename')} onClick=${() => run(() => setRenamingKey(tab.key))} />
                <${MenuItem} icon="IconChevronLeft" text=${t('dashboardParity.tabs.moveLeft')} disabled=${index === 0} onClick=${() => run(() => actions.onMove(tab.key, -1))} />
                <${MenuItem} icon="IconChevronRight" text=${t('dashboardParity.tabs.moveRight')} disabled=${index === tabs.length - 1} onClick=${() => run(() => actions.onMove(tab.key, 1))} />
                <${MenuItem} icon="IconCopyPlus" text=${t('dashboardParity.tabs.duplicate')} disabled=${tabs.length >= MAXIMUM_DASHBOARD_TABS} onClick=${() => run(() => actions.onDuplicate(tab.key))} />
                <${DropdownMenuSeparator} />
                <${MenuItem} icon="IconTrash" accent="danger" text=${t('dashboardParity.tabs.delete')} disabled=${tabs.length <= 1} onClick=${() => run(() => actions.onRemove(tab.key))} />
              <//>
            <//>`
          : null}
      </div>`;
    })}
    ${isEditing && actions
      ? html`<button type="button" class="dashboard-tab-add" aria-label=${t('dashboardParity.tabs.add')} title=${t('dashboardParity.tabs.add')}
          disabled=${tabs.length >= MAXIMUM_DASHBOARD_TABS} onClick=${() => {
            actions.onAdd();
          }}><${Icon} name="IconPlus" size=${16} /></button>`
      : null}
  </div>`;
}

/**
 * Chave da aba sob o ponteiro (soltar um widget arrastado sobre a aba move para ela).
 * @param {number} clientX @param {number} clientY
 */
export function findTabDropTarget(clientX, clientY) {
  const element = /** @type {HTMLElement | null} */ (document.elementFromPoint(clientX, clientY)?.closest('[data-dashboard-tab-drop]') ?? null);
  return element?.dataset.dashboardTabDrop ?? null;
}
